import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import CmsRichEditor from "../../components/RichEditor";

const SECTION_TYPES = [
  { value: "wallet", label: "Wallet" },
  { value: "giftcard", label: "Gift Card" },
  { value: "referral", label: "Referral" },
  { value: "app_section", label: "App Section" },
  { value: "credit_line", label: "Credit Line" },
  { value: "terms_and_condition", label: "Terms and Condition" },
  { value: "privacy_policy", label: "Privacy Policy" },
];

export default function PolicyPageForm() {
  const navigate = useNavigate();
  const { policy_page_id } = useParams();

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  const [form, setForm] = useState({
    title: "",
    description: "",
    section_type: "",
    status: "1",
  });

  const fetchPage = async () => {
    try {
      setFetching(true);

      const res = await api.get(`/admin/policy-pages/${policy_page_id}`);
      const data = res.data?.data;

      setForm({
        title: data?.title || "",
        description: data?.description || "",
        section_type: data?.section_type || "",
        status: String(data?.status ?? "1"),
      });
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed to load policy page");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (policy_page_id) fetchPage();
  }, [policy_page_id]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!form.title.trim() || !form.description.trim() || !form.section_type) {
      toast.error("Title, description and section type are required");
      return;
    }

    try {
      setLoading(true);

      if (policy_page_id) {
        await api.patch(`/admin/policy-pages/${policy_page_id}`, {
          title: form.title,
          description: form.description,
          section_type: form.section_type,
          status: Number(form.status),
        });
        toast.success("Policy page updated");
      } else {
        await api.post("/admin/policy-pages", {
          title: form.title,
          description: form.description,
          section_type: form.section_type,
          status: Number(form.status),
        });
        toast.success("Policy page created");
      }

      navigate("/admin/policy-pages");
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">
            {policy_page_id ? "Edit Policy Page" : "Create Policy Page"}
          </h1>
        </div>

        <button
          type="button"
          className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50 self-start"
          onClick={() => navigate("/admin/policy-pages")}
        >
          Back
        </button>
      </div>

      {fetching ? (
        <div className="mt-6 text-sm text-gray-600">Loading page...</div>
      ) : (
        <form onSubmit={onSubmit} className="mt-6 border rounded-2xl p-5 space-y-4">
          <div>
            <label className="text-sm font-semibold">Title</label>
            <input
              name="title"
              value={form.title}
              onChange={onChange}
              className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
              placeholder="Ex: Wallet Refund Policy"
            />
          </div>

          <div className="max-w-[240px]">
            <label className="text-sm font-semibold">Section Type</label>
            <select
              name="section_type"
              value={form.section_type}
              onChange={onChange}
              className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            >
              <option value="">Select section type</option>
              {SECTION_TYPES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-semibold">Description</label>
            <div className="mt-2">
              <CmsRichEditor
                value={form.description}
                onChange={(html) => setForm((p) => ({ ...p, description: html }))}
              />
            </div>
          </div>

          <div className="max-w-[240px]">
            <label className="text-sm font-semibold">Status</label>
            <select
              name="status"
              value={form.status}
              onChange={onChange}
              className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            >
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <button
              type="submit"
              disabled={loading}
              className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {loading ? "Saving..." : "Save"}
            </button>

            <button
              type="button"
              className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
              onClick={() => navigate("/admin/policy-pages")}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
